/**
 * CustomerVehicleStep — combined orderer + vehicle selection (#1002)
 *
 * One unified search input fills two persistent slots (orderer + vehicle) in
 * either order. Typing searches persons, companies and vehicles at once;
 * create actions are always offered below the results. Selecting a vehicle
 * first suggests its linked party as the orderer (visible and changeable);
 * selecting an orderer first lists their vehicles up-front (#939). A plate
 * with no local match auto-triggers the registry lookup (#562) and continues
 * into the prefilled inline vehicle form. New persons/companies are drafted
 * inline in the customer details card via the shared form-field components and
 * saved by the wizard's step-1 Next button (#1020) — this component renders
 * the drafts and their (lifted) validation errors but never persists them.
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Alert,
  Typography,
  Button,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Menu,
  Paper,
  Tooltip,
  Stack,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { format } from 'date-fns';
import { VehicleFormFields, type VehicleFieldsData } from '@/components/vehicles/VehicleFormFields';
import { InlineVehicleFields, type InlineVehicleFieldsVehicle } from '@/components/vehicles/InlineVehicleFields';
import { VehicleDetailModal } from '@/components/vehicles/VehicleDetailModal';
import { personToPartyRef, companyToPartyRef, type PartyRef } from '@/components/parties/PartyPicker';
import {
  PersonFormFields,
  initialPersonFieldsData,
  type PersonFieldsData,
} from '@/components/persons/PersonFormFields';
import {
  CompanyFormFields,
  initialCompanyFieldsData,
  type CompanyFieldsData,
} from '@/components/companies/CompanyFormFields';
import {
  useLatestMileage,
  useVehicle,
  useVehicleHistory,
  type ServiceHistoryEntry as VehicleHistoryApiEntry,
  type VehicleDetail,
  type VehicleListItem,
} from '@/hooks/useVehicles';
import type { Customer, ServiceHistoryEntry, TraficomData, VehicleDetails } from '@/types';
import { useCompanyPersonLinks } from '@/hooks/useCompanies';
import { type Person } from '@/hooks/usePersons';
import { type Company } from '@/hooks/useCompanies';
import { type VehicleLookupResult } from '@/hooks/useVehicleLookup';
import { useNotify } from '@/hooks/useNotify';
import { guideAnchor } from '@/lib/guides/engine';
import type { WorkOrderPartyRole } from '@/lib/workOrderPartyRoles';
import { mergeExternalVehicleData } from './mergeExternalVehicleData';
import {
  UnifiedCustomerVehicleSearch,
  isPlateLike,
  formatPlate,
  parsePersonName,
  isBusinessIdLike,
  type SearchMode,
} from './UnifiedCustomerVehicleSearch';
import { AdditionalPartiesSection } from './AdditionalPartiesSection';
import { resolveMileageEmphasis } from './mileageEmphasis';
import { VehicleInspectionSummary } from './VehicleInspectionSummary';
import { formatNumber } from '@/utils/locale';

export type { PartyRef };

export type InlinePartyCreate = 'person' | 'company' | null;

/** Orderer suggestion from a picked vehicle's linked party (#939, #2899). */
export function ordererPartyFromVehicle(vehicle: VehicleListItem): PartyRef | null {
  if (!vehicle.partyType || !(vehicle.personId || vehicle.companyId)) return null;
  return {
    partyType: vehicle.partyType,
    personId: vehicle.personId ?? null,
    companyId: vehicle.companyId ?? null,
    name: vehicle.customerName,
    phone: vehicle.customerPhone || null,
  };
}

/** Exported for tests: missing stored numbers show the i18n placeholder, never a TM- id fallback (#2640, #2907). */
export function mapVehicleServiceHistory(
  data: VehicleHistoryApiEntry[] | undefined,
  noWorkOrderNumber: string,
): ServiceHistoryEntry[] {
  if (!data) return [];
  return data.map((entry) => ({
    id: entry.id,
    workOrderNumber: entry.workOrderNumber ?? noWorkOrderNumber,
    date: entry.completedAt ?? entry.scheduledAt,
    mileage: entry.mileage ?? 0,
    services: (entry.serviceType ?? '').split(', ').filter(Boolean),
    totalCents: entry.totalCostCents ?? 0,
    mechanicName: entry.mechanicName ?? '',
  }));
}

function customerFromVehicleDetail(detail: VehicleDetail): Customer {
  if (detail.customer) {
    return {
      id: detail.customer.id,
      firstName: detail.customer.firstName ?? '',
      lastName: detail.customer.lastName ?? '',
      email: detail.customer.email ?? '',
      phone: detail.customer.phone ?? '',
    };
  }
  return {
    id: detail.customerId,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  };
}

export function toVehicleDetails(detail: VehicleDetail, history: ServiceHistoryEntry[]): VehicleDetails {
  const traficom = detail.traficomData as TraficomData | undefined;
  const mapped: VehicleDetails = {
    id: detail.id,
    customerId: detail.customerId,
    registrationNumber: detail.registrationNumber,
    make: detail.make ?? '',
    model: detail.model ?? '',
    year: detail.year ?? 0,
    customer: customerFromVehicleDetail(detail),
    serviceCount: history.length,
    totalSpentCents: history.reduce((sum, entry) => sum + entry.totalCents, 0),
    createdAt: detail.createdAt,
  };
  if (detail.vin) mapped.vin = detail.vin;
  if (detail.mileage != null) mapped.mileage = detail.mileage;
  if (detail.notes) mapped.notes = detail.notes;
  if (traficom) {
    mapped.traficomData = traficom;
    if (traficom.nextInspectionDue) mapped.motExpiryDate = traficom.nextInspectionDue;
  }
  if (detail.lastServiceDate) mapped.lastServiceAt = detail.lastServiceDate;
  if (detail.owner) mapped.owner = detail.owner;
  if (detail.holder) mapped.holder = detail.holder;
  mapped.workOrderers = detail.workOrderers;
  return mapped;
}

function toVehicleDetailsFromList(vehicle: VehicleListItem): VehicleDetails {
  const nameParts = vehicle.customerName.trim().split(' ');
  const mapped: VehicleDetails = {
    id: vehicle.id,
    customerId: vehicle.customerId,
    registrationNumber: vehicle.registrationNumber,
    make: vehicle.make ?? '',
    model: vehicle.model ?? '',
    year: vehicle.year ?? 0,
    customer: {
      id: vehicle.customerId,
      firstName: nameParts[0] ?? '',
      lastName: nameParts.slice(1).join(' ') || '',
      email: '',
      phone: vehicle.customerPhone,
    },
    serviceCount: 0,
    totalSpentCents: 0,
    createdAt: vehicle.createdAt,
  };
  if (vehicle.vin) mapped.vin = vehicle.vin;
  if (vehicle.mileage != null) mapped.mileage = vehicle.mileage;
  if (vehicle.notes) mapped.notes = vehicle.notes;
  if (vehicle.traficomData) {
    const traficom = vehicle.traficomData as unknown as TraficomData;
    mapped.traficomData = traficom;
    if (traficom.nextInspectionDue) mapped.motExpiryDate = traficom.nextInspectionDue;
  }
  return mapped;
}

interface CustomerVehicleStepProps {
  // Unified search input state
  searchInput: string;
  setSearchInput: (v: string) => void;

  // Vehicle slot state
  resolvedVehicle: VehicleListItem | null;
  setResolvedVehicle: (v: VehicleListItem | null) => void;
  isCreatingNewVehicle: boolean;
  setIsCreatingNewVehicle: (v: boolean) => void;
  mileageInput: string;
  setMileageInput: (v: string) => void;

  // New vehicle state (inline form + external lookup pre-fill)
  newVehicleData: VehicleFieldsData;
  setNewVehicleData: (fn: (prev: VehicleFieldsData) => VehicleFieldsData) => void;
  newVehicleErrors: Partial<Record<keyof VehicleFieldsData, string>>;
  setNewVehicleErrors: (fn: (prev: Partial<Record<keyof VehicleFieldsData, string>>) => Partial<Record<keyof VehicleFieldsData, string>>) => void;

  // Orderer party state (#833 B1)
  ordererParty: PartyRef | null;
  setOrdererParty: (p: PartyRef | null) => void;
  /** Where the orderer came from — vehicle owner link or a manual pick. */
  ordererSource: 'vehicle' | 'manual' | null;
  setOrdererSource: (s: 'vehicle' | 'manual' | null) => void;
  /** Registry person id for the primary contact (company orderers). */
  contactPersonRegistryId: string;
  setContactPersonRegistryId: (v: string) => void;

  // Inline person/company creation (#1002/#1020) — drafts AND their validation
  // errors live in the modal: drafts survive the step collapsing (StepContent
  // unmounts on exit) and the step-1 Next button validates/saves them there.
  inlinePartyCreate: InlinePartyCreate;
  setInlinePartyCreate: (v: InlinePartyCreate) => void;
  personDraft: PersonFieldsData;
  setPersonDraft: (fn: (prev: PersonFieldsData) => PersonFieldsData) => void;
  personErrors: Partial<Record<keyof PersonFieldsData, string>>;
  setPersonErrors: (fn: (prev: Partial<Record<keyof PersonFieldsData, string>>) => Partial<Record<keyof PersonFieldsData, string>>) => void;
  companyDraft: CompanyFieldsData;
  setCompanyDraft: (fn: (prev: CompanyFieldsData) => CompanyFieldsData) => void;
  companyErrors: Partial<Record<keyof CompanyFieldsData, string>>;
  setCompanyErrors: (fn: (prev: Partial<Record<keyof CompanyFieldsData, string>>) => Partial<Record<keyof CompanyFieldsData, string>>) => void;

  // Additional (optional) work order party roles (#1108) — persisted after
  // the work order is created via PUT /work-orders/:id/parties/:role.
  additionalParties: Partial<Record<WorkOrderPartyRole, PartyRef>>;
  onAddParty: (role: WorkOrderPartyRole, party: PartyRef) => void;
  onRemoveParty: (role: WorkOrderPartyRole) => void;

  // Common state
  setIsDirty: (v: boolean) => void;
}

export function CustomerVehicleStep({
  searchInput,
  setSearchInput,
  resolvedVehicle,
  setResolvedVehicle,
  isCreatingNewVehicle,
  setIsCreatingNewVehicle,
  mileageInput,
  setMileageInput,
  newVehicleData,
  setNewVehicleData,
  newVehicleErrors,
  setNewVehicleErrors,
  ordererParty,
  setOrdererParty,
  ordererSource,
  setOrdererSource,
  contactPersonRegistryId,
  setContactPersonRegistryId,
  inlinePartyCreate,
  setInlinePartyCreate,
  personDraft,
  setPersonDraft,
  personErrors,
  setPersonErrors,
  companyDraft,
  setCompanyDraft,
  companyErrors,
  setCompanyErrors,
  additionalParties,
  onAddParty,
  onRemoveParty,
  setIsDirty,
}: CustomerVehicleStepProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const { t: tv } = useTranslation('vehicles');
  const notify = useNotify();
  const [vehicleCardOpen, setVehicleCardOpen] = useState(false);

  const mileageInputRef = useRef<HTMLInputElement>(null);

  // "Change customer/vehicle" live behind 3-dot overflow menus so the
  // sensitive, relatively-rare actions don't shout from the slot cards (#552).
  const [ordererActionsAnchor, setOrdererActionsAnchor] = useState<HTMLElement | null>(null);
  const [vehicleActionsAnchor, setVehicleActionsAnchor] = useState<HTMLElement | null>(null);

  // Registry lookup result behind the current inline vehicle form — display only.
  const [externalVehicleData, setExternalVehicleData] = useState<VehicleLookupResult | null>(null);

  // Latest mileage for resolved vehicle
  const { data: latestMileage } = useLatestMileage(resolvedVehicle?.id);
  const { data: vehicleDetail } = useVehicle(vehicleCardOpen ? resolvedVehicle?.id : undefined);
  const { data: historyData } = useVehicleHistory(vehicleCardOpen ? resolvedVehicle?.id : undefined);
  const vehicleCardHistory = useMemo(
    () => mapVehicleServiceHistory(historyData?.data, t('noWorkOrderNumber')),
    [historyData, t],
  );
  const vehicleCardDetails = useMemo(() => {
    if (!resolvedVehicle) return null;
    if (vehicleDetail) return toVehicleDetails(vehicleDetail, vehicleCardHistory);
    return toVehicleDetailsFromList(resolvedVehicle);
  }, [resolvedVehicle, vehicleDetail, vehicleCardHistory]);

  // --- Slot / mode derivation ---
  const hasVehicleSlot = resolvedVehicle !== null || isCreatingNewVehicle;
  const hasOrdererSlot = ordererParty !== null || inlinePartyCreate !== null;
  const searchMode: SearchMode | null =
    !hasOrdererSlot && !hasVehicleSlot
      ? 'both'
      : !hasVehicleSlot
        ? 'vehicleOnly'
        : !hasOrdererSlot
          ? 'partyOnly'
          : null;

  // Contact person selection for company orderers: linked persons of the company.
  const isCompanyOrderer = ordererParty?.partyType === 'company';
  const { data: personLinksData, isLoading: isLoadingContacts } = useCompanyPersonLinks(
    isCompanyOrderer ? (ordererParty?.companyId ?? undefined) : undefined
  );
  const contactLinks = useMemo(() => {
    const links = personLinksData?.data ?? [];
    // Only active links can act as contacts.
    return links.filter((l) => l.validTo === null);
  }, [personLinksData]);

  // Auto-select the primary contact for company orderers.
  useEffect(() => {
    if (isCompanyOrderer && contactLinks.length > 0 && !contactPersonRegistryId) {
      const primary = contactLinks.find((l) => l.isPrimary);
      if (primary) {
        setContactPersonRegistryId(primary.personId);
      }
    }
  }, [isCompanyOrderer, contactLinks, contactPersonRegistryId, setContactPersonRegistryId]);

  // --- Selection handlers ---

  const handleVehicleSelect = useCallback((vehicle: VehicleListItem) => {
    setResolvedVehicle(vehicle);
    // Adopt the vehicle's linked party as the orderer ONLY when none is
    // selected yet (vehicle-first flow) — a visible, changeable suggestion.
    // If an orderer is already chosen or being created, keep it (#939).
    if (!ordererParty && inlinePartyCreate === null) {
      const party = ordererPartyFromVehicle(vehicle);
      if (party) {
        setOrdererParty(party);
        setOrdererSource('vehicle');
        setContactPersonRegistryId('');
      }
    }
    setIsCreatingNewVehicle(false);
    setIsDirty(true);

    // Focus mileage input after vehicle selection
    requestAnimationFrame(() => mileageInputRef.current?.focus());
  }, [ordererParty, inlinePartyCreate, setResolvedVehicle, setOrdererParty, setOrdererSource, setContactPersonRegistryId, setIsCreatingNewVehicle, setIsDirty]);

  const handlePersonSelect = useCallback((person: Person) => {
    setOrdererParty(personToPartyRef(person));
    setOrdererSource('manual');
    setContactPersonRegistryId('');
    setIsDirty(true);
  }, [setOrdererParty, setOrdererSource, setContactPersonRegistryId, setIsDirty]);

  const handleCompanySelect = useCallback((company: Company) => {
    setOrdererParty(companyToPartyRef(company));
    setOrdererSource('manual');
    setContactPersonRegistryId('');
    setIsDirty(true);
  }, [setOrdererParty, setOrdererSource, setContactPersonRegistryId, setIsDirty]);

  const handleCreateVehicle = useCallback((reg: string, lookup: VehicleLookupResult | null) => {
    if (lookup) {
      setExternalVehicleData(lookup);
      setNewVehicleData((prev) => mergeExternalVehicleData(prev, reg, lookup));
      notify.info(t('create.externalVehicleDataUsed'));
    } else {
      setExternalVehicleData(null);
      setNewVehicleData((prev) => ({ ...prev, registrationNumber: reg }));
    }
    setIsCreatingNewVehicle(true);
    setIsDirty(true);
  }, [setNewVehicleData, setIsCreatingNewVehicle, setIsDirty, notify, t]);

  // A registry lookup selected while still in flight resolved after the fact —
  // merge into the already-open create form (registry values only fill empty fields).
  const handleLookupResolved = useCallback((reg: string, lookup: VehicleLookupResult) => {
    setExternalVehicleData(lookup);
    setNewVehicleData((prev) =>
      mergeExternalVehicleData(prev, prev.registrationNumber || formatPlate(reg), lookup),
    );
    notify.info(t('create.externalVehicleDataUsed'));
  }, [setNewVehicleData, notify, t]);

  const handleCreatePersonAction = useCallback((input: string) => {
    const { firstName, lastName } = parsePersonName(input);
    setPersonDraft(() => ({ ...initialPersonFieldsData, firstName, lastName }));
    setPersonErrors(() => ({}));
    setInlinePartyCreate('person');
    setIsDirty(true);
  }, [setPersonDraft, setPersonErrors, setInlinePartyCreate, setIsDirty]);

  const handleCreateCompanyAction = useCallback((input: string) => {
    const trimmed = input.trim();
    setCompanyDraft(() =>
      isBusinessIdLike(trimmed)
        ? { ...initialCompanyFieldsData, businessId: trimmed }
        : { ...initialCompanyFieldsData, name: trimmed },
    );
    setCompanyErrors(() => ({}));
    setInlinePartyCreate('company');
    setIsDirty(true);
  }, [setCompanyDraft, setCompanyErrors, setInlinePartyCreate, setIsDirty]);

  const handleCancelInlineCreate = useCallback(() => {
    setInlinePartyCreate(null);
    setPersonDraft(() => ({ ...initialPersonFieldsData }));
    setCompanyDraft(() => ({ ...initialCompanyFieldsData }));
    setPersonErrors(() => ({}));
    setCompanyErrors(() => ({}));
  }, [setInlinePartyCreate, setPersonDraft, setCompanyDraft, setPersonErrors, setCompanyErrors]);

  const handleChangeOrderer = useCallback(() => {
    setOrdererActionsAnchor(null);
    setOrdererParty(null);
    setOrdererSource(null);
    setContactPersonRegistryId('');
  }, [setOrdererParty, setOrdererSource, setContactPersonRegistryId]);

  const handleCloseVehicleCard = useCallback(() => {
    setVehicleCardOpen(false);
  }, []);

  const handleChangeVehicle = useCallback(() => {
    setVehicleActionsAnchor(null);
    setVehicleCardOpen(false);
    setResolvedVehicle(null);
    setMileageInput('');
    // A vehicle-sourced orderer suggestion goes away with the vehicle.
    if (ordererSource === 'vehicle') {
      setOrdererParty(null);
      setOrdererSource(null);
      setContactPersonRegistryId('');
    }
    setIsDirty(true);
  }, [setResolvedVehicle, setMileageInput, ordererSource, setOrdererParty, setOrdererSource, setContactPersonRegistryId, setIsDirty]);

  // An inline edit of the chosen vehicle (#2120) is saved to the API by
  // InlineVehicleFields. Mirror it into the wizard's copy, or a corrected
  // plate reverts when the step remounts and the summary shows the old one (#2883).
  const handleInlineVehicleSaved = useCallback((saved: InlineVehicleFieldsVehicle) => {
    if (!resolvedVehicle || resolvedVehicle.id !== saved.id) return;
    setResolvedVehicle({
      ...resolvedVehicle,
      registrationNumber: saved.registrationNumber,
      make: saved.make ?? null,
      model: saved.model ?? null,
      year: saved.year ?? null,
      ...(saved.notes !== undefined ? { notes: saved.notes } : {}),
    });
  }, [resolvedVehicle, setResolvedVehicle]);

  const handleCancelNewVehicle = useCallback(() => {
    setIsCreatingNewVehicle(false);
    setNewVehicleErrors(() => ({}));
    setExternalVehicleData(null);
  }, [setIsCreatingNewVehicle, setNewVehicleErrors]);

  // Mileage is highlighted when empty or lower than the latest recorded
  // reading. Emphasis is visual only — it does not block Next (#2114).
  const mileageEmphasis = resolveMileageEmphasis(mileageInput, latestMileage?.mileage);

  const showDetails = hasOrdererSlot || hasVehicleSlot;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Unified search — one input for orderer AND vehicle, hidden once both slots are filled */}
      {searchMode && (
        <Box sx={{ maxWidth: 560 }}>
          <UnifiedCustomerVehicleSearch
            mode={searchMode}
            inputValue={searchInput}
            onInputChange={(v) => {
              setSearchInput(v);
              if (v) setIsDirty(true);
            }}
            ordererParty={ordererParty}
            onVehicleSelect={handleVehicleSelect}
            onPersonSelect={handlePersonSelect}
            onCompanySelect={handleCompanySelect}
            onCreateVehicle={handleCreateVehicle}
            onCreatePerson={handleCreatePersonAction}
            onCreateCompany={handleCreateCompanyAction}
            onLookupResolved={handleLookupResolved}
            autoFocus
          />
          {/* Blank new vehicle without a plate search (e.g. unregistered vehicle) */}
          {searchMode !== 'partyOnly' && (
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={() =>
                handleCreateVehicle(isPlateLike(searchInput) ? formatPlate(searchInput) : '', null)
              }
              sx={{ mt: 1 }}
            >
              {t('create.createNewVehicle')}
            </Button>
          )}
        </Box>
      )}

      {/* Slot cards: orderer on the left, vehicle on the right */}
      {showDetails && (
        <>
        <Grid container spacing={3}>
          {/* Left column: Orderer details */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                {t('create.customerDetails')}
              </Typography>

              {/* Selected orderer party */}
              {ordererParty && (
                <Box>
                  <Alert
                    severity="success"
                    icon={false}
                    action={
                      <Tooltip title={t('create.customerActions')}>
                        <IconButton
                          size="small"
                          onClick={(e) => setOrdererActionsAnchor(e.currentTarget)}
                          aria-label={t('create.customerActions')}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <Typography variant="subtitle2">
                      {ordererSource === 'vehicle' ? t('create.customerResolved') : t('create.customerSelected')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={ordererParty.partyType === 'company' ? t('parties.company') : t('parties.person')}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Typography variant="body2">
                        {ordererParty.name}
                        {ordererParty.phone ? ` · ${ordererParty.phone}` : ''}
                      </Typography>
                    </Box>
                  </Alert>
                  <Menu
                    anchorEl={ordererActionsAnchor}
                    open={Boolean(ordererActionsAnchor)}
                    onClose={() => setOrdererActionsAnchor(null)}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  >
                    <MenuItem onClick={handleChangeOrderer}>
                      <SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />
                      {t('create.changeCustomer')}
                    </MenuItem>
                  </Menu>
                </Box>
              )}

              {/* Inline person creation (#1002) — shared fields with PersonFormDialog */}
              {!ordererParty && inlinePartyCreate === 'person' && (
                <Stack spacing={2} {...guideAnchor('wo-create.newPerson')}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('create.inlineCreatePersonTitle')}
                  </Typography>
                  <PersonFormFields
                    data={personDraft}
                    errors={personErrors}
                    onChange={(field, value) => {
                      setPersonDraft((prev) => ({ ...prev, [field]: value }));
                      if (personErrors[field]) setPersonErrors((prev) => ({ ...prev, [field]: undefined }));
                      setIsDirty(true);
                    }}
                    showExtended={false}
                    autoFocus
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('create.inlineCreateSavedOnNext')}
                  </Typography>
                  <Box>
                    <Button size="small" color="inherit" onClick={handleCancelInlineCreate}>
                      {tc('cancel')}
                    </Button>
                  </Box>
                </Stack>
              )}

              {/* Inline company creation (#1002) — shared fields with CompanyFormDialog */}
              {!ordererParty && inlinePartyCreate === 'company' && (
                <Stack spacing={2} {...guideAnchor('wo-create.newCompany')}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('create.inlineCreateCompanyTitle')}
                  </Typography>
                  <CompanyFormFields
                    data={companyDraft}
                    errors={companyErrors}
                    onChange={(field, value) => {
                      setCompanyDraft((prev) => ({ ...prev, [field]: value }));
                      if (companyErrors[field]) setCompanyErrors((prev) => ({ ...prev, [field]: undefined }));
                      setIsDirty(true);
                    }}
                    showExtended={false}
                    autoFocus
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('create.inlineCreateSavedOnNext')}
                  </Typography>
                  <Box>
                    <Button size="small" color="inherit" onClick={handleCancelInlineCreate}>
                      {tc('cancel')}
                    </Button>
                  </Box>
                </Stack>
              )}

              {/* Vehicle chosen but no orderer yet — guide to the search box */}
              {!ordererParty && inlinePartyCreate === null && (
                <Typography variant="body2" color="text.secondary">
                  {t('create.ordererMissingHint')}
                </Typography>
              )}

              {/* Contact Person (company orderers) — from the company's person links */}
              {isCompanyOrderer && (
                <Box {...guideAnchor('wo-create.companyContact')} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <ContactPhoneIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('create.contactPerson')}
                    </Typography>
                  </Box>
                  {isLoadingContacts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : contactLinks.length === 0 ? (
                    <Alert severity="info">{t('create.noContactPersons')}</Alert>
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('create.selectContactPerson')}</InputLabel>
                      <Select
                        value={contactPersonRegistryId}
                        label={t('create.selectContactPerson')}
                        onChange={(e) => {
                          setContactPersonRegistryId(e.target.value);
                          setIsDirty(true);
                        }}
                      >
                        <MenuItem value="">
                          <em>-</em>
                        </MenuItem>
                        {contactLinks.map((link) => (
                          <MenuItem key={link.id} value={link.personId}>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {[link.firstName, link.lastName].filter(Boolean).join(' ')}
                                {link.isPrimary ? ' *' : ''}
                              </Typography>
                              {link.role && (
                                <Typography variant="caption" color="text.secondary">
                                  {link.role}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Right column: Vehicle details */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                {t('create.vehicleDetails')}
              </Typography>

              {/* Vehicle found — show details */}
              {resolvedVehicle && (
                <Box>
                  <Alert
                    severity="success"
                    icon={false}
                    action={
                      <Tooltip title={t('create.vehicleActions')}>
                        <IconButton
                          size="small"
                          onClick={(e) => setVehicleActionsAnchor(e.currentTarget)}
                          aria-label={t('create.vehicleActions')}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <Typography variant="subtitle2">{t('create.vehicleFound')}</Typography>
                    <Typography variant="body2">
                      {resolvedVehicle.registrationNumber}
                      {resolvedVehicle.make || resolvedVehicle.model
                        ? ` — ${[resolvedVehicle.make, resolvedVehicle.model, resolvedVehicle.year].filter(Boolean).join(' ')}`
                        : ''}
                    </Typography>
                  </Alert>
                  <Menu
                    anchorEl={vehicleActionsAnchor}
                    open={Boolean(vehicleActionsAnchor)}
                    onClose={() => setVehicleActionsAnchor(null)}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  >
                    <MenuItem onClick={handleChangeVehicle}>
                      <SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />
                      {t('create.changeVehicle')}
                    </MenuItem>
                  </Menu>

                  <Box sx={{ mt: 2 }}>
                    <InlineVehicleFields
                      vehicle={{
                        id: resolvedVehicle.id,
                        registrationNumber: resolvedVehicle.registrationNumber,
                        make: resolvedVehicle.make,
                        model: resolvedVehicle.model,
                        year: resolvedVehicle.year,
                        notes: resolvedVehicle.notes ?? null,
                      }}
                      onSaved={handleInlineVehicleSaved}
                    />
                    <VehicleInspectionSummary
                      traficomData={{
                        nextInspectionDue:
                          typeof resolvedVehicle.traficomData?.['nextInspectionDue'] === 'string'
                            ? resolvedVehicle.traficomData['nextInspectionDue']
                            : null,
                      }}
                    />
                    <Button
                      size="small"
                      onClick={() => { setVehicleCardOpen(true); }}
                      sx={{ mt: 1 }}
                    >
                      {tv('openFullCard')}
                    </Button>
                  </Box>

                  {/* Mileage input */}
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      inputRef={mileageInputRef}
                      label={t('create.mileageLabel')}
                      placeholder={t('create.mileagePlaceholder')}
                      value={mileageInput}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setMileageInput(val);
                        setIsDirty(true);
                      }}
                      type="text"
                      inputMode="numeric"
                      size="small"
                      fullWidth
                      {...(mileageEmphasis.kind === 'missing'
                        ? { color: 'error' as const, focused: true }
                        : mileageEmphasis.kind === 'lowerThanLatest'
                          ? { color: 'warning' as const, focused: true }
                          : {})}
                      helperText={
                        latestMileage?.mileage != null && latestMileage.recordedAt
                          ? t('create.mileageLatest', {
                              mileage: formatNumber(latestMileage.mileage),
                              date: format(new Date(latestMileage.recordedAt), 'd.M.yyyy'),
                            })
                          : t('create.mileageNoHistory')
                      }
                    />
                    {mileageEmphasis.kind === 'lowerThanLatest' && (
                      <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ mt: 1 }}>
                        {t('create.mileageLowerThanLatest', {
                          latest: formatNumber(mileageEmphasis.latest),
                        })}
                      </Alert>
                    )}
                  </Box>
                </Box>
              )}

              {/* Creating new vehicle — inline form (same step, no separate wizard step) */}
              {isCreatingNewVehicle && (
                <Box {...guideAnchor('wo-create.newVehicle')} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert
                    severity={externalVehicleData ? 'success' : 'info'}
                    icon={false}
                    action={
                      <Button size="small" onClick={handleCancelNewVehicle}>
                        {tc('cancel')}
                      </Button>
                    }
                  >
                    <Typography variant="subtitle2">
                      {externalVehicleData ? t('create.externalVehicleDataUsed') : t('create.createNewVehicle')}
                    </Typography>
                    {externalVehicleData && (
                      <Typography variant="caption" color="text.secondary">
                        {t('create.externalVehicleSource', { provider: externalVehicleData.provider })}
                      </Typography>
                    )}
                  </Alert>
                  <VehicleFormFields
                    data={newVehicleData}
                    errors={newVehicleErrors}
                    onChange={(field, value) => {
                      setNewVehicleData((prev) => ({ ...prev, [field]: value }));
                      if (newVehicleErrors[field]) {
                        setNewVehicleErrors((prev) => ({ ...prev, [field]: undefined }));
                      }
                      setIsDirty(true);
                    }}
                  />
                </Box>
              )}

              {/* Orderer chosen but no vehicle yet — guide to the search box */}
              {!resolvedVehicle && !isCreatingNewVehicle && (
                <Typography variant="body2" color="text.secondary">
                  {t('create.vehicleMissingHint')}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        <AdditionalPartiesSection
          parties={additionalParties}
          onAddParty={onAddParty}
          onRemoveParty={onRemoveParty}
          setIsDirty={setIsDirty}
        />
        </>
      )}

      {vehicleCardOpen && vehicleCardDetails && (
        <VehicleDetailModal
          vehicle={vehicleCardDetails}
          serviceHistory={vehicleCardHistory}
          open={vehicleCardOpen}
          onClose={handleCloseVehicleCard}
          hideCreateWorkOrder
        />
      )}
    </Box>
  );
}
